import { CSSProperties } from 'react'
import { useFrequencyData } from '../../utils/useFrequencyData'
import { getBars } from './BarsVisualization'

interface BaseRadialBarsProps {
  innerRadius: number
  lineThickness?: number
  barOrigin?: 'outer' | 'inner' | 'middle'
  roundness?: number
  color?: CSSProperties['color']
  maxAmplitude?: number
}

interface RadialBarsProps extends BaseRadialBarsProps {
  values: number[]
  radius: number
}

export const RadialBars: React.FC<RadialBarsProps> = ({
  values,
  radius,
  lineThickness,
  roundness,
  barOrigin,
  color,
  maxAmplitude = 1,
  innerRadius,
}) => {
  if (!values || values.length === 0) {
    return null
  }

  const diameter = 2 * radius

  return (
    <div style={{ width: diameter, height: diameter }}>
      <svg
        width={diameter}
        height={diameter}
        viewBox={`0 0 ${diameter} ${diameter}`}
      >
        {values.map((v, i) => {
          const barHeight = (v * (radius - innerRadius)) / maxAmplitude
          const x = radius
          const y =
            barOrigin === 'outer'
              ? radius - barHeight
              : barOrigin === 'inner'
                ? radius
                : radius - 0.5 * barHeight
          const yOffset =
            barOrigin === 'outer'
              ? radius
              : barOrigin === 'inner'
                ? innerRadius
                : radius - 0.5 * (radius - innerRadius)

          const transform = `rotate(${
            (360 * i) / values.length
          } ${radius} ${radius}) translate(0 ${yOffset})`

          return (
            <rect
              key={i}
              fill={color}
              x={x}
              y={y}
              width={lineThickness}
              height={barHeight}
              rx={roundness}
              transform={transform}
            />
          )
        })}
      </svg>
    </div>
  )
}

interface RadialBarsVisualizationProps extends BaseRadialBarsProps {
  diameter: number
  gapSize?: number
  maxDb?: number
  mediaStart?: number | undefined
  mediaUrl: string
  minDb?: number
  waveformUrl?: string | null | undefined
}

export const RadialBarsVisualization: React.FC<
  RadialBarsVisualizationProps
> = ({
  barOrigin = 'inner',
  color = 'white',
  diameter,
  gapSize = 4,
  innerRadius = 0,
  lineThickness = 8,
  maxAmplitude,
  maxDb,
  mediaUrl,
  mediaStart,
  minDb,
  roundness = 4,
  waveformUrl,
}) => {
  const radius = 0.5 * diameter
  const frequencyData = useFrequencyData(mediaUrl, mediaStart, waveformUrl)

  if (!frequencyData) return null

  const bars = getBars({
    totalWidth: 2 * Math.PI * innerRadius,
    itemWidth: lineThickness + gapSize,
    frequencyData,
    maxDb,
    minDb,
  })

  const highpass = bars.slice(Math.floor(0.5 * bars.length))

  const amplitudes = [...highpass, ...highpass.slice().reverse()]

  return (
    <RadialBars
      values={amplitudes}
      radius={radius}
      lineThickness={lineThickness}
      roundness={roundness}
      barOrigin={barOrigin}
      color={color}
      maxAmplitude={maxAmplitude}
      innerRadius={innerRadius}
    />
  )
}
